<template>
  <v-card elevation="0" class="ai-content-and-share-tab rounded-0" :color="$vuetify.theme.dark ? '#0b1929' : '#E0EAF2'">
    <v-row no-gutters>
      <!-- LEFT SIDE: LIST + SERVER PAGINATION -->
      <v-col cols="12" md="4" lg="3" class="left-side-tabs">
        <v-card flat class="pa-3 h-100 d-flex flex-column" :color="$vuetify.theme.dark ? '#131c29' : '#ffffff'">

          <div>
            <h3 class="mb-1 font-weight-medium" :style="{ color: $vuetify.theme.dark ? '#E0EAF2' : '#132136' }">
              📰 All News
            </h3>
            <p class="text-caption mb-3" :style="{ color: $vuetify.theme.dark ? '#9fb3c8' : '#607083' }">
              Click an item to view details
            </p>
            <v-divider class="mb-2"></v-divider>
          </div>

          <!-- Scrollable list -->
          <div class="scrollable-list flex-grow-1">
            <v-list dense>
              <v-list-item v-for="item in news" :key="item.id" @click="fetchDetails(item)"
                class="d-flex justify-space-between align-center news-list-item"
                :class="{ 'news-list-item--active': selectedNews?.id === item.id }" style="cursor: pointer;">
                <v-list-item-content>
                  <v-list-item-title class="news-title" :style="{ color: $vuetify.theme.dark ? '#E0EAF2' : '#132136' }">
                    {{ item.name }}
                  </v-list-item-title>

                  <v-list-item-subtitle class="news-subtitle"
                    :style="{ color: $vuetify.theme.dark ? '#9fb3c8' : '#6b7c90' }">
                    {{ formatDateShort(item.created_at) }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <v-icon small color="#02b58a">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>

              <v-list-item v-if="!news.length">
                <v-list-item-content>
                  <v-list-item-title class="text-caption"
                    :style="{ color: $vuetify.theme.dark ? '#9fb3c8' : '#6b7c90' }">
                    No news found.
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- SERVER PAGINATION -->
          <div class="mt-3 d-flex justify-center">
            <v-pagination v-model="page" :length="pagination.totalPages" :total-visible="5" circle :color="'#02b58a'"
              prev-icon="mdi-chevron-left" next-icon="mdi-chevron-right" @input="changePage" />
          </div>

        </v-card>
      </v-col>

      <!-- RIGHT SIDE: DETAILS -->
      <v-col cols="12" md="8" lg="9" class="d-flex flex-column right-side-tabs">
        <v-card flat class="h-100 d-flex flex-column" :color="$vuetify.theme.dark ? '#0b1929' : '#E0EAF2'">

          <div class="chat-body px-5 py-4 flex-grow-1">
            <!-- When news is selected -->
            <template v-if="selectedNews">
              <div class="news-header mb-4">
                <h2 class="mb-1" :style="{ color: $vuetify.theme.dark ? '#E0EAF2' : '#132136' }">
                  {{ selectedNews.name }}
                </h2>

                <div class="d-flex flex-wrap align-center text-caption">
                  <span class="mr-4" :style="{ color: $vuetify.theme.dark ? '#9fb3c8' : '#6b7c90' }">
                    Category:
                    <strong>{{ selectedNews.category_name || "Uncategorized" }}</strong>
                  </span>

                  <span :style="{ color: $vuetify.theme.dark ? '#9fb3c8' : '#6b7c90' }">
                    Published:
                    <strong>{{ formatDate(selectedNews.created_at) }}</strong>
                  </span>
                </div>
              </div>

              <!-- White Bubble -->
              <div class="news-bubble pa-5" :style="{
                background: $vuetify.theme.dark ? '#1b2a3d' : '#ffffff',
                color: $vuetify.theme.dark ? '#E0EAF2' : '#132136',
              }">
                <div v-html="selectedNews.description"></div>
              </div>
            </template>

            <template v-else>
              <div class="text-center py-10">
                <v-icon size="60" color="grey lighten-1">
                  mdi-newspaper-variant-outline
                </v-icon>
                <p :style="{ color: $vuetify.theme.dark ? '#9fb3c8' : '#6b7c90' }">
                  Please select a news item from the left
                </p>
              </div>
            </template>
          </div>

        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { NEWS__ACTIONS } from "@/store/action-types";
import toast from "@/services/Notification";

export default {
  data() {
    return {
      selectedNews: null,
      page: 1,
    };
  },

  computed: {
    ...mapState("news", ["news", "pagination"]),
  },

  watch: {
    page(newVal) {
      this.changePage(newVal);
    },
  },

  methods: {
    ...mapActions("news", [NEWS__ACTIONS.GETNEWS, NEWS__ACTIONS.VIEWNEWSDETAILS]),

    async changePage(p) {
      try {
        await this[NEWS__ACTIONS.GETNEWS]({ page: p, limit: 10 });

        // Auto-select first item of page
        if (this.news.length > 0) {
          this.fetchDetails(this.news[0]);
        }
      } catch (err) {
        console.error(err);
      }
    },

    async fetchDetails(item) {
      try {
        const res = await this[NEWS__ACTIONS.VIEWNEWSDETAILS](item.id);

        if (res?.data?.success) {
          this.selectedNews = res.data.data;
        } else {
          this.selectedNews = item;
        }
      } catch (error) {
        toast.error("Failed to load news details");
        this.selectedNews = item;
      }
    },

    formatDateShort(dateStr) {
      return new Date(dateStr).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatDate(dateStr) {
      return new Date(dateStr).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },

  async mounted() {
    await this.changePage(1);
  },
};
</script>

<style scoped>
.left-side-tabs {
  min-height: 500px;
}

.scrollable-list {
  max-height: 500px;
  overflow-y: auto;
}

.scrollable-list::-webkit-scrollbar {
  width: 6px;
}

.scrollable-list::-webkit-scrollbar-thumb {
  background-color: #132136;
  border-radius: 10px;
}

.news-list-item {
  border-radius: 6px;
  margin-bottom: 4px;
  transition: 0.15s ease;
}

.news-list-item:hover {
  transform: translateY(-1px);
  background-color: rgba(2, 181, 138, 0.08);
}

.news-list-item--active {
  background-color: rgba(2, 181, 138, 0.25) !important;
}

.news-title {
  font-size: 0.9rem;
  font-weight: 500;
}

.news-subtitle {
  font-size: 0.75rem;
}

.news-bubble {
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}
</style>
